import {
  onboardingTemplateRemoveBlockedDetailsSchema,
  type OnboardingCaseKind,
} from '@gb-ticket/shared-types';
import { useState } from 'react';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { ApiRequestError } from '@/lib/api';
import { ONBOARDING_CASE_KIND_LABEL } from '@/products/onboarding/features/onboarding-template-labels';
import {
  useDeleteOnboardingTemplate,
  useUpdateOnboardingTemplate,
} from '@/products/onboarding/features/use-onboarding-templates';

export function OnboardingTemplateDeleteButton({
  template,
  onDeleted,
}: {
  template: { id: string; name: string; kind: OnboardingCaseKind; isActive: boolean };
  onDeleted?: () => void;
}) {
  const remove = useDeleteOnboardingTemplate();
  const update = useUpdateOnboardingTemplate(template.id);
  const [open, setOpen] = useState(false);
  const [blocked, setBlocked] = useState<{ caseCount: number; isActive: boolean } | null>(null);
  const label = `${template.name} (${ONBOARDING_CASE_KIND_LABEL[template.kind]})`;
  const pending = remove.isPending || update.isPending;

  async function confirmDelete() {
    try {
      await remove.mutateAsync(template.id);
      setOpen(false);
      toast.success(`${label} wurde gelöscht.`);
      onDeleted?.();
    } catch (error) {
      const details =
        error instanceof ApiRequestError
          ? onboardingTemplateRemoveBlockedDetailsSchema.safeParse(error.error.details)
          : null;
      if (details?.success) {
        setBlocked({ caseCount: details.data.caseCount, isActive: details.data.isActive });
        return;
      }
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Vorlage konnte nicht gelöscht werden.',
      );
    }
  }

  async function activate() {
    try {
      await update.mutateAsync({ isActive: true });
      toast.success(`${label} ist wieder aktiv.`);
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Vorlage konnte nicht aktiviert werden.',
      );
    }
  }

  async function deactivate() {
    try {
      await update.mutateAsync({ isActive: false });
      setOpen(false);
      toast.success(`${label} wurde deaktiviert. Bestehende Vorgänge bleiben.`);
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Vorlage konnte nicht deaktiviert werden.',
      );
    }
  }

  return (
    <>
      <div className="flex flex-wrap gap-2">
        {template.isActive ? null : (
          <Button
            type="button"
            variant="outline"
            className="min-h-11"
            disabled={pending}
            onClick={() => void activate()}
          >
            Aktivieren
          </Button>
        )}
        <Button
          type="button"
          variant="outline"
          className="min-h-11"
          onClick={() => {
            setBlocked(null);
            setOpen(true);
          }}
        >
          Löschen
        </Button>
      </div>
      <AlertDialog
        open={open}
        onOpenChange={(next) => {
          if (!next && !pending) {
            setOpen(false);
            setBlocked(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {blocked ? `${label} bleibt gespeichert` : `${label} löschen?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {blocked
                ? `Die Vorlage hängt an ${blocked.caseCount} ${blocked.caseCount === 1 ? 'Vorgang' : 'Vorgängen'}. Phasen, Aufgaben und die Historie dieser Vorgänge bleiben. ${
                    blocked.isActive
                      ? 'Eine Deaktivierung nimmt die Vorlage aus neuen Vorgängen.'
                      : 'Die Vorlage ist bereits deaktiviert und bleibt für diese Vorgänge gespeichert.'
                  }`
                : 'Gelöscht wird nur diese Vorlage, und nur wenn kein Vorgang eine ihrer Versionen verwendet. Laufende und abgeschlossene Vorgänge werden nicht mitgelöscht.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={pending}>Abbrechen</AlertDialogCancel>
            {blocked?.isActive ? (
              <Button
                type="button"
                className="min-h-11"
                disabled={pending}
                onClick={() => void deactivate()}
              >
                Deaktivieren
              </Button>
            ) : null}
            {blocked ? null : (
              <Button
                type="button"
                variant="destructive"
                className="min-h-11"
                disabled={pending}
                onClick={() => void confirmDelete()}
              >
                {remove.isPending ? 'Wird gelöscht …' : 'Löschen'}
              </Button>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
